import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as forumApi from '../api/forum';
import { useAuth } from '../lib/auth';
import { isCategoryModerator } from '../lib/forumModeration';
import { useToast } from '../components/toast';
import Modal from '../components/Modal';
import CategoryForm, { emptyCategoryForm, categoryToForm } from '../components/CategoryForm';
import { ErrorAlert, Spinner, EmptyState, Field } from '../components/ui';
import { cn, ui } from '../lib/ui';

/** True when a category is gated to verified accredited investors. */
function isAccreditedOnly(category) {
  return Boolean(category.accreditedOnly || category.accessCriteria?.accreditation === 'verified');
}

export default function Forum() {
  const navigate = useNavigate();
  const { user, isStaff } = useAuth();
  const isAdmin = user?.role === 'admin';
  const toast = useToast();

  const [categories, setCategories] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  // Admin category management (FEA-19). `creating` opens the blank form;
  // `editingCategory` holds the category being edited (prefills the same
  // form + isActive). Non-admin staff get a single searchability toggle
  // instead — `searchabilityBusyId` tracks that in-flight PUT.
  const [creating, setCreating] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchabilityBusyId, setSearchabilityBusyId] = useState(null);

  const loadCategories = useCallback(() => {
    forumApi
      .listCategories()
      .then(d => setCategories(d.categories || []))
      .catch(err => {
        // Resolve the list on failure too — leaving it null kept the spinner
        // up forever under the error alert (FND-44 fallout, "nothing renders").
        setCategories([]);
        setError(err);
      });
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  function onSearch(event) {
    event.preventDefault();
    const q = query.trim();
    if (q.length < 2) return;
    navigate(`/forum/search?q=${encodeURIComponent(q)}`);
  }

  async function createCategory(payload) {
    const { category } = await forumApi.createCategory(payload);
    toast.success(`Category "${category.name}" created.`);
    setCreating(false);
    loadCategories();
  }

  async function saveCategory(payload) {
    if (!editingCategory) return;
    const { category } = await forumApi.updateCategory(editingCategory.categoryId, payload);
    toast.success(`Category "${category.name}" updated.`);
    setEditingCategory(null);
    loadCategories();
  }

  async function toggleSearchability(category) {
    setSearchabilityBusyId(category.categoryId);
    try {
      const { category: updated } = await forumApi.setCategorySearchability(
        category.categoryId,
        !category.externallySearchable
      );
      setCategories(current =>
        current.map(c =>
          c.categoryId === updated.categoryId
            ? { ...c, externallySearchable: updated.externallySearchable }
            : c
        )
      );
      toast.success(
        updated.externallySearchable
          ? 'Category is now shown in search results for members who otherwise can’t access it.'
          : 'Category removed from those search results.'
      );
    } catch (err) {
      toast.error(err);
    } finally {
      setSearchabilityBusyId(null);
    }
  }

  return (
    <>
      <div className={cn(ui.row, ui.spread, 'mb-4 items-start')}>
        <h1 className="text-title-l">Community forum</h1>
        {isAdmin && (
          <button type="button" className={ui.btnPrimary} onClick={() => setCreating(true)}>
            New category
          </button>
        )}
      </div>
      <form onSubmit={onSearch} className={cn(ui.row, 'mb-4')} role="search">
        <Field
          label="Search the forum"
          placeholder="Search threads and posts…"
          style={{ minWidth: '18rem' }}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <button className={ui.btnSecondary} type="submit" disabled={query.trim().length < 2}>
          Search
        </button>
      </form>
      <ErrorAlert error={error} />
      {categories === null ? (
        <Spinner />
      ) : categories.length === 0 ? (
        <EmptyState>No forum categories yet.</EmptyState>
      ) : (
        <div className={cn(ui.grid, ui.gridMd2)}>
          {categories.map(c => (
            <div key={c.categoryId} className={ui.card}>
              <Link
                to={`/forum/categories/${c.categoryId}`}
                className="block text-inherit no-underline"
              >
                <h3 className={ui.h3}>
                  {c.name}
                  {isAccreditedOnly(c) && <span className={ui.pillWarning}> Accredited only</span>}
                  {/* Staff-only: inactive categories are hidden from everyone else, but
                      canAccessCategory() bypasses the isActive check for ALL staff
                      (not just admins) — so any staff role can land here and should
                      see the state, not just admins. */}
                  {isStaff && !c.isActive && <span className={ui.pillNeutral}> Inactive</span>}
                </h3>
                {c.description && <p className={cn(ui.muted, 'text-body-s')}>{c.description}</p>}
                {typeof c.threadCount === 'number' && (
                  <p className="text-body-s">{c.threadCount} threads</p>
                )}
              </Link>
              {isAdmin ? (
                <div className={cn(ui.row, 'mt-4')}>
                  <button
                    type="button"
                    className={ui.btnSecondary}
                    onClick={() => setEditingCategory(c)}
                  >
                    Edit
                  </button>
                </div>
              ) : (
                // FEA-30: staff OR a member this category designates a
                // moderator (docs/FRONTEND-PLAN.md §4.3) may flip this toggle
                // — category admin (Edit, above) stays admin-only.
                isCategoryModerator(c, { userId: user?.userId, isStaff }) && (
                  <div className={cn(ui.row, 'mt-4')}>
                    <button
                      type="button"
                      className={ui.btnSecondary}
                      disabled={searchabilityBusyId === c.categoryId}
                      onClick={() => toggleSearchability(c)}
                    >
                      {searchabilityBusyId === c.categoryId
                        ? 'Working…'
                        : c.externallySearchable
                          ? 'Remove from search'
                          : 'Show in search'}
                    </button>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}

      {isAdmin && (
        <Modal open={creating} onClose={() => setCreating(false)} title="New category">
          <CategoryForm
            initial={emptyCategoryForm()}
            submitLabel="Create category"
            showIsActive={false}
            onSubmit={createCategory}
            onCancel={() => setCreating(false)}
          />
        </Modal>
      )}

      {isAdmin && (
        <Modal
          open={Boolean(editingCategory)}
          onClose={() => setEditingCategory(null)}
          title={editingCategory ? `Edit "${editingCategory.name}"` : 'Edit category'}
        >
          {editingCategory && (
            <CategoryForm
              key={editingCategory.categoryId}
              initial={categoryToForm(editingCategory)}
              submitLabel="Save changes"
              showIsActive
              onSubmit={saveCategory}
              onCancel={() => setEditingCategory(null)}
            />
          )}
        </Modal>
      )}
    </>
  );
}
